import React, { useEffect, useState } from 'react';
import { useIsSuperadmin } from '../hooks/useIsSuperadmin';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuth } from '../../../contexts/AuthContext';
import Spinner from '../../../components/ui/Spinner';
import { AppIcon } from '../../../components/icons/AppIcon';
import { MOBILE_SHELL_TOP_SAFE_CLASS } from '../../../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';
import { SuperadminSidebar } from './SuperadminSidebar';
import { fetchAdminDashboardStats } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

interface SuperadminLayoutProps {
    children: React.ReactNode;
}

export function SuperadminLayout({ children }: SuperadminLayoutProps) {
    const { isSuperadmin, isLoading: isSuperadminLoading, refetch } = useIsSuperadmin();
    const { isAuthLoading } = useAuth();
    const { setCurrentPage, currentPage } = useAppContext();
    const [isProbing, setIsProbing] = useState(false);
    const [probeAttempted, setProbeAttempted] = useState(false);
    const [navOpen, setNavOpen] = useState(false);

    const isLoading = isAuthLoading || isSuperadminLoading;
    const needsProbe = !isLoading && !isSuperadmin && !probeAttempted;

    useEffect(() => {
        if (!needsProbe) return;

        let cancelled = false;
        setIsProbing(true);
        setProbeAttempted(true);

        void (async () => {
            try {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setMonth(start.getMonth() - 12);
                start.setHours(0, 0, 0, 0);
                await fetchAdminDashboardStats({
                    from: start.toISOString(),
                    to: end.toISOString(),
                });
                if (cancelled) return;
                await refetch();
            } catch (e) {
                if (cancelled) return;
                setCurrentPage('dashboard');
                const message =
                    e instanceof SuperadminApiError && e.isForbidden
                        ? 'Acesso não autorizado à área de superadmin.'
                        : 'Não foi possível verificar acesso à área de superadmin.';
                notify.warning(message);
            } finally {
                if (!cancelled) setIsProbing(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [needsProbe, refetch, setCurrentPage]);

    useEffect(() => {
        setNavOpen(false);
    }, [currentPage]);

    useEffect(() => {
        if (!navOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setNavOpen(false);
        };
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [navOpen]);

    if (isLoading || isProbing || needsProbe) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <Spinner color="primary" size="lg" />
            </div>
        );
    }

    if (!isSuperadmin) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <Spinner color="primary" size="lg" />
            </div>
        );
    }

    return (
        <div className="flex h-dvh flex-col bg-background md:flex-row">
            <header
                className={cn(
                    'flex shrink-0 items-center gap-3 border-b border-subtle bg-card px-4 py-3 md:hidden',
                    MOBILE_SHELL_TOP_SAFE_CLASS,
                )}
            >
                <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-secondary transition-colors hover:bg-surface-subtle hover:text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-expanded={navOpen}
                    aria-controls="superadmin-nav"
                    aria-label={navOpen ? 'Fechar menu' : 'Abrir menu'}
                    onClick={() => setNavOpen((v) => !v)}
                >
                    <AppIcon name={navOpen ? 'close' : 'menu'} className="h-5 w-5" />
                </button>
                <p className="min-w-0 truncate text-sm font-semibold text-default">Superadmin</p>
            </header>

            {navOpen && (
                <button
                    type="button"
                    className="fixed inset-0 z-40 bg-black/40 md:hidden"
                    aria-label="Fechar menu"
                    onClick={() => setNavOpen(false)}
                />
            )}

            <SuperadminSidebar open={navOpen} onClose={() => setNavOpen(false)} />

            <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:p-6">
                {children}
            </main>
        </div>
    );
}
